import { NgFor, NgIf, AsyncPipe } from '@angular/common';
import { Component, Inject, OnDestroy } from '@angular/core';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { AgrUser, CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { Customer } from '@next/core-lib/business-partner-aggregation-api';
import { PageToolbarComponent } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { map, skipWhile, take } from 'rxjs/operators';

import { ViewMode } from 'src/app/shared/models/shared.model';

import { TailBiting, TailBitingDataSource, LocationItem } from '../../model/tail-biting.model';
import { OfflineService } from '../../services/offline.service';
import * as actions from '../../store/actions/tail-biting.actions';
import { getTailBitings, getSelectedTailBiting } from '../../store/selectors/tail-biting.selectors';

import { TailBitingDetailComponent } from '../tail-biting-detail/tail-biting-detail.component';

@Component({
  selector: 'app-tail-biting-overview',
  templateUrl: './tail-biting-overview.component.html',
  styleUrls: ['./tail-biting-overview.component.scss'],
  imports: [
    PageToolbarComponent,
    MatSelect,
    NgFor,
    MatOption,
    NgIf,
    TableComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class TailBitingOverviewComponent implements OnDestroy {
  serviceWorkerRegistration: ServiceWorkerRegistration;
  pageTitle = this.localeService.translate('tail-biting.overview.title');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  upsertupsertTailBitingOfflineObservable$: Observable<any>;
  upsertupsertTailBitingOfflineObservableSubscription: Subscription;
  onlineEvent$: Observable<Event>;

  customer$: Observable<Customer> = this.store.select(UserSelectors.selectCustomer);

  isMobile$ = this.breakpointService.ltSm$;

  locations$: Observable<SelectedLocation[]> = this.store.select(UserSelectors.selectLocations);

  locationItems: LocationItem[];
  selectedLocations: LocationItem[];

  selectedLocation: number;
  locations: SelectedLocation[];
  customer: Customer;

  tableColumnClass: string;
  tailBitingDatasource: TailBitingDataSource[];

  private readonly tableConfigBase: TableConfig = {
    dataSource: undefined,
    columns: undefined,
    selectColumn: false,
    actionColumn: true,
    pagination: false,
    disableCards: true,
    footer: false,
    scrolling: true,
    wrapperWidth: '100%',
  };

  public tableConfig$: Observable<TableConfig> = this.store.select(getTailBitings).pipe(
    skipWhile((fs) => !fs),
    map((tailBitings: TailBiting[]) => {
      let tailBitingList: TailBiting[] = [];
      if (tailBitings) {
        tailBitingList = tailBitings;
      }

      if (!tailBitingList) {
        return undefined;
      }

      const tableConfig: TableConfig = {
        ...this.tableConfigBase,
        columns: [
          {
            id: 'name',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('tail-biting.overview.table.tailBitingName'),
          },
          {
            id: 'date',
            type: TableColumnTypes.DATE,
            label: this.localeService.translate('tail-biting.overview.table.tailBitingDate'),
          },
          {
            id: 'createdBy',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('tail-biting.overview.table.tailBitingAdvisor'),
            headerClassName: this.tableColumnClass,
            className: this.tableColumnClass,
          },
          {
            id: 'numberOfPens',
            type: TableColumnTypes.NUMBER,
            label: this.localeService.translate(
              'tail-biting.overview.table.tailBitingNumberOfPens',
            ),
          },
          {
            id: 'pigType',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('tail-biting.overview.table.tailBitingPigType'),
          },
          {
            id: 'ages',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('tail-biting.overview.table.tailBitingAges'),
          },
          {
            id: 'location',
            type: TableColumnTypes.STRING,
            label: this.localeService.translate('tail-biting.overview.table.tailBitingLocation'),
          },
        ],
        actions: [
          {
            id: 'edit',
            callback: (row: TailBiting) => this.openTailBitingEdit(row.id),
            label: 'Edit',
            icon: 'edit',
            disabled: () => false,
            primary: false,
          },
          {
            id: 'delete',
            callback: (row: TailBiting) => this.deleteTailBiting(row),
            label: 'Delete',
            icon: 'delete',
            disabled: () => false,
            primary: false,
          },
        ],
      };

      if (tailBitings) {
        this.createTailBitingsDatasource(tailBitings);

        tableConfig.dataSource = new MatTableDataSource(this.tailBitingDatasource);
      } else {
        tableConfig.dataSource = new MatTableDataSource([]);
      }
      return tableConfig;
    }),
  );

  constructor(
    @Inject(BreakpointServiceInjector)
    private readonly breakpointService: BreakpointService,
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private readonly dialog: DialogService,
    private readonly offlineService: OfflineService,
  ) {
    this.isMobile$.pipe(take(1)).subscribe((isMobile) => {
      this.tableColumnClass = isMobile ? 'mobile' : '';
    });

    this.locations$
      .pipe(
        skipWhile((l) => !l),
        take(2),
      )
      .subscribe((locations) => {
        this.locations = locations;
        this.locationItems = locations.map(
          (userlocation) =>
            <LocationItem>{
              value: userlocation.id,
              name: userlocation.name,
              selected: userlocation.isSelected,
            },
        );
        this.selectedLocations = this.locationItems;
      });

    this.customer$
      .pipe(
        take(2),
        skipWhile((customer) => customer === undefined || customer === null),
      )
      .subscribe((customer) => {
        this.customer = customer;
        this.upsertupsertTailBitingOfflineObservable$ =
          this.offlineService.getOfflineDataObservable$(customer.id, 'UpsertTailBiting');
      });

    this.upsertupsertTailBitingOfflineObservableSubscription =
      this.upsertupsertTailBitingOfflineObservable$?.subscribe((a) => {
        if (window.navigator.onLine && a?.type === 'RETRIEVE_OFFLINE_DATA') {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const requests: TailBiting[] = a?.payload?.map((payload: any) => payload?.payload);

          let lastrequest: TailBiting = null;

          requests.forEach((request: TailBiting) => {
            if (lastrequest && request.id === undefined && request.name === lastrequest.name) {
              this.store
                .select(getSelectedTailBiting)
                .pipe(
                  skipWhile((c) => !c),
                  take(1),
                )
                .subscribe((tailBiting) => {
                  request.id = tailBiting.id;

                  this.store.dispatch(
                    actions.upsertTailBitingRequest({
                      request,
                    }),
                  );
                });
            } else {
              this.store.dispatch(
                actions.upsertTailBitingRequest({
                  request,
                }),
              );
            }

            lastrequest = request;
          });

          this.clearCachedData();

          this.store.dispatch(actions.clearTailBitingState());
          this.store.dispatch(actions.getTailBitingsRequest());
        }
      });

    this.onlineEvent$ = fromEvent(window, 'online');
    this.onlineEvent$.subscribe(() => {
      // handle online mode
      this.getCachedData();
    });
  }

  ngOnDestroy(): void {
    if (this.upsertupsertTailBitingOfflineObservableSubscription) {
      this.upsertupsertTailBitingOfflineObservableSubscription.unsubscribe();
    }
  }

  getCachedData() {
    this.customer$
      .pipe(
        take(1),
        skipWhile((customer) => customer === undefined || customer === null),
      )
      .subscribe((customer) => {
        this.offlineService.dispatchGetOfflineData(customer.id, 'UpsertTailBiting');
      });
  }

  clearCachedData() {
    this.customer$
      .pipe(
        take(1),
        skipWhile((customer) => customer === undefined || customer === null),
      )
      .subscribe((customer) => {
        this.offlineService.dispatchDeleteOfflineData(customer.id, 'UpsertTailBiting');
      });
  }

  isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);

  createTailBiting() {
    this.store.dispatch(actions.clearSelectedTailBiting());
    this.store
      .select(UserSelectors.selectUser)
      .pipe(take(1))
      .subscribe((user: AgrUser) => {
        this.dialog
          .select({
            options: this.locationItems,
            selected: null,
            emptyText: 'No options available',
            pushHistoryState: false,
            title: this.localeService.translate('tail-biting.detail.create.chooselocation.title'),
          })
          .subscribe(({ status, result }) => {
            if (status === DIALOG_RETURN_TYPES.confirm) {
              this.selectedLocation = result;
              this.dialog
                .fullscreenDialog(
                  TailBitingDetailComponent,
                  {
                    confirmButton: true,
                    confirmButtonText: this.localeService.translate('save'),
                    altButton: true,
                    altButtonText: this.localeService.translate(
                      'tail-biting.detail.save-and-create-new',
                    ),
                    altButtonColor: 'primary',
                    altButtonIcon: 'save',
                    setPendingChanges: false,
                    tailBitingId: null,
                    locationId: this.selectedLocation,
                    locations: this.locations,
                    customer: this.customer,
                    viewMode: ViewMode.CREATE,
                  },
                  {
                    maxWidth: '100vw',
                  },
                )
                .subscribe((result) => {
                  if (result.status === DIALOG_RETURN_TYPES.confirm) {
                    const request = result.result.tailBiting;
                    request.createdBy = user.name;
                    request.createdAt = new Date().toISOString();
                    request.date = new Date().toISOString();
                    this.store.dispatch(actions.upsertTailBitingRequest({ request }));
                  }
                });
            }
          });
      });
  }

  openTailBitingEdit(tailBitingId: number) {
    this.store.dispatch(
      actions.setSelectedTailBitingRequest({
        id: tailBitingId,
      }),
    );
    this.dialog
      .fullscreenDialog(
        TailBitingDetailComponent,
        {
          confirmButton: true,
          confirmButtonText: this.localeService.translate('save'),
          altButton: true,
          altButtonText: this.localeService.translate('tail-biting.detail.save-and-create-new'),
          altButtonColor: 'primary',
          altButtonIcon: 'save',
          setPendingChanges: false,
          tailBitingId,
          locations: this.locations,
          customer: this.customer,
          viewMode: ViewMode.EDIT,
        },
        {
          maxWidth: '100vw',
        },
      )
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          this.store.dispatch(
            actions.upsertTailBitingRequest({ request: result.result.tailBiting }),
          );
          this.store.dispatch(actions.loadData());
        }
      });
  }

  deleteTailBiting(tailBiting: TailBiting) {
    this.store.dispatch(actions.deleteTailBitingWithConfirmation({ tailBiting }));
  }

  // helper function to set selected Locations in the multiselect in the header.
  // We need to use a compareWith function because it does not work automatically in this case.
  areLocationsEqual(li1: LocationItem, li2: LocationItem): boolean {
    if (!li1 || !li2) {
      return false;
    }
    return li1.name === li2.name && li1.value === li2.value && li1.selected === li2.selected;
  }

  changeLocation(selection: MatSelectChange) {
    const selected = (<LocationItem[]>selection.value).map((val) => val.value);
    const feedAdviceDataSource = this.tailBitingDatasource?.filter((x) =>
      selected.some((id) => x.locationId === id),
    );

    if (feedAdviceDataSource) {
      this.tableConfig$ = this.tableConfig$.pipe(
        map((tableConfig) => ({
          ...tableConfig,
          dataSource: new MatTableDataSource(feedAdviceDataSource),
        })),
      );
    }
  }

  private createTailBitingsDatasource(tailBitings: TailBiting[]) {
    this.tailBitingDatasource = tailBitings.map(
      (tailBiting: TailBiting) =>
        ({
          id: tailBiting.id,
          name: tailBiting.name,
          date: tailBiting.date,
          createdBy: tailBiting.createdBy,
          pigType: this.localeService.translate('tail-biting.detail.pigType.' + tailBiting.pigType),
          tailDocking: tailBiting.tailDocking,
          numberOfPens: tailBiting.pens.length,

          ages: tailBiting.pens
            .map((pen) => pen.age)
            .filter((x, i, a) => x && a.indexOf(x) === i)
            .sort(function (x, y) {
              return x - y;
            })
            .toString()
            .replace(/,/g, ', '),
          location: this.locations.find((l) => l.id === tailBiting.locationId)?.name,
          locationId: this.locations.find((l) => l.id === tailBiting.locationId)?.id,
        }) as TailBitingDataSource,
    );
  }
}
